import {
  Box,
  Checkbox,
  CheckboxGroup,
  Heading,
  Radio,
  RadioGroup,
  Stack,
} from '@chakra-ui/react';

import { Loading } from '@revelio/core';
import { FormField, Input, Select, Switch, Text } from '@revelio/ui';

import type {
  DataBuilderSelectOption,
  DataBuilderSelectSection,
  DataBuilderSettingsGroup,
  DataBuilderSettingsSection,
} from '../../DataBuilder/api/data-builder-api.types';
import type { SettingsValue, SettingsValues } from './helpers/settings-value';
import { asBool, asString, asStringArray } from './helpers/settings-value';

export const DataBuilderSettingsForm = ({
  groups,
  values,
  setValue,
  errors,
  isLoading,
  isError,
  readonly,
}: {
  groups: DataBuilderSettingsGroup[];
  values: SettingsValues;
  setValue: (key: string, value: SettingsValue) => void;
  errors?: Record<string, string>;
  isLoading: boolean;
  isError: boolean;
  readonly?: boolean;
}) => {
  if (isLoading) {
    return (
      <Box mt="2">
        <Loading />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box mt="2">
        <Text fontSize="sm">
          Failed to load Data Builder settings. Please try again.
        </Text>
      </Box>
    );
  }

  const renderSelect = (section: DataBuilderSelectSection) => {
    const options = section.options as DataBuilderSelectOption[];
    const renderHint = section.render ?? 'dropdown';
    const testId = `db-settings-${section.key}`;

    if (renderHint === 'radio' && !section.multi) {
      return (
        <RadioGroup
          value={asString(values[section.key])}
          onChange={(value) => setValue(section.key, value)}
          isDisabled={readonly}
          data-testid={testId}
        >
          <Stack>
            {options.map((option) => (
              <Radio key={option.value} value={option.value}>
                {option.label}
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      );
    }

    if (renderHint === 'checkbox' && section.multi) {
      return (
        <CheckboxGroup
          value={asStringArray(values[section.key])}
          onChange={(value) => setValue(section.key, value as string[])}
        >
          <Stack data-testid={testId}>
            {options.map((option) => (
              <Checkbox
                key={option.value}
                value={option.value}
                isDisabled={readonly}
              >
                {option.label}
              </Checkbox>
            ))}
          </Stack>
        </CheckboxGroup>
      );
    }

    const placeholder = `Select ${section.label}...`;

    if (section.multi) {
      const selected = options.filter((option) =>
        asStringArray(values[section.key]).includes(option.value)
      );
      return (
        <Select<DataBuilderSelectOption, true>
          isMulti
          isClearable
          isDisabled={readonly}
          options={options}
          value={selected}
          onChange={(selectedOptions) =>
            setValue(
              section.key,
              selectedOptions?.map((option) => option.value) ?? []
            )
          }
          placeholder={placeholder}
          data-testid={testId}
        />
      );
    }

    const selected =
      options.find(
        (option) => option.value === asString(values[section.key])
      ) ?? null;
    return (
      <Select<DataBuilderSelectOption, false>
        isClearable
        isDisabled={readonly}
        options={options}
        value={selected}
        onChange={(selectedOption) =>
          setValue(section.key, selectedOption?.value ?? '')
        }
        placeholder={placeholder}
        data-testid={testId}
      />
    );
  };

  const renderControl = (section: DataBuilderSettingsSection) => {
    switch (section.type) {
      case 'boolean':
        return (
          <Switch
            inactiveLabel="Off"
            label="On"
            checked={asBool(values[section.key])}
            onChange={(event) => setValue(section.key, event.target.checked)}
            disabled={readonly}
            data-testid={`db-settings-${section.key}`}
          />
        );
      case 'select':
        return renderSelect(section);
      case 'text':
        return (
          <Input
            type="text"
            value={asString(values[section.key])}
            onChange={(event) => setValue(section.key, event.target.value)}
            disabled={readonly}
            isInvalid={Boolean(errors?.[section.key])}
            data-testid={`db-settings-${section.key}`}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {groups.map((group) => (
        <Stack
          key={group.key}
          width="100%"
          borderWidth="1px"
          borderRadius="md"
          p="2"
          mt="2"
          boxShadow="sm"
          color="gray.800"
        >
          <Heading as="h5" size="sm" color="text.primary">
            {group.label}
          </Heading>
          {group.sections.map((section) => {
            const control = renderControl(section);
            if (control === null) {
              return null;
            }
            const fieldError = errors?.[section.key];
            return (
              <Box mt="2" key={section.key}>
                <FormField
                  label={section.label}
                  supportingText={section.description ?? undefined}
                  errors={fieldError ? [fieldError] : undefined}
                >
                  {control}
                </FormField>
              </Box>
            );
          })}
        </Stack>
      ))}
    </>
  );
};
